import { FC, useEffect, useState } from "react";
import {
  ApprovedBy,
  AuthClient,
  Campaign,
  CampaignClient,
  Category,
  EditUserDto,
  GroupAccountBalanceClient,
  GroupAccountBalanceDto,
  InvestmentType,
  TokenDto,
  UserRegistrationDto,
  UsersClient,
} from "../../utilities/backend/client";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { DevTool } from "@hookform/devtools";
import ContentTitle from "../../components/ContentTitle";
import CloseIcon from "@mui/icons-material/Close";
import PrivacyPolicy from "../app/PrivacyPolicy";
import TermsAndConditions from "../app/TermsAndConditions";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import InvestFeedback from "../app/User/InvestFeedback";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../utilities/redux/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { removeToken, setRole, setToken } from "../../utilities/redux/actions";
import useAutoLogin from "../../hooks/useAutoLogin";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const Register = () => {
  const authClient = new AuthClient();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [notificationError, setNotificationError] = useState<{ status: boolean, text?: string }>({ status: false });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [investmentInProcessShow, setInvestmentInProcessShow] = useState<boolean>(false);
  const [investFeedbackShow, setInvestFeedbackShow] = useState<boolean>(false);
  const [sentFeedback, setSentFeedback] = useState<boolean>(false);
  const userClient = new UsersClient();
  const campaignClient = new CampaignClient();
  const [user, setUser] = useState<EditUserDto>({} as EditUserDto);
  const [allThemes, setAllThemes] = useState<Category[]>();
  const [campain, setCampain] = useState<Campaign>({} as Campaign);
  const [investmentTypes, setInvestmentTypes] = useState<InvestmentType[]>();
  const [approvedBy, setApprovedBy] = useState<ApprovedBy[]>();
  const groupAccountBalanceClient = new GroupAccountBalanceClient();
  const [groupAccountBalance, setGroupAccountBalanceClient] =
    useState<GroupAccountBalanceDto>({} as GroupAccountBalanceDto);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const token: TokenDto = {
    token: useSelector((state: RootState) => state.token.token) || "",
  };

  const [searchParams] = useSearchParams();

  const proceedToLogin = useAutoLogin({ setLoading });

  const form = useForm<UserRegistrationDto>({
    defaultValues: {
      firstName: "",
      lastName: "",
      userName: "",
      password: "",
      email: "",
      phoneNumber: "",
    },
    mode: "onChange",
  });

  const { register, handleSubmit, formState, control, setValue, setError } =
    form;
  const { errors } = formState;

  const handlePhoneNumberChange = (event: any) => {
    let phoneNumber = event.target.value.replace(/\D/g, "").slice(0, 10);

    setValue("phoneNumber", phoneNumber);

    const validationError = validatePhoneNumber(phoneNumber);
    if (validationError !== true) {
      setError("phoneNumber", {
        type: "manual",
        message: validationError,
      });
    } else {
      form.clearErrors("phoneNumber");
    }
  };

  const handleTermsOpen = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setTermsModalOpen(true);
    return false;
  };

  const handlePrivacyOpen = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setPrivacyModalOpen(true);
    return false;
  };

  const handleModalClose = () => {
    setTermsModalOpen(false);
    setPrivacyModalOpen(false);
  };

  const validateEmail = (value: string | null | undefined) => {
    if (!value) return "Email is required";
    if (value === "ken@arizae.com")
      return "Invalid email. Please, use different email";

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) {
      return "Invalid email format. Template: someone@mail.com";
    }

    return true;
  };

  const validatePhoneNumber = (value: string | null | undefined) => {
    if (!value) return "Phone number is required";
    if (
      typeof value !== "string" ||
      !value.match(/^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/)
    )
      return "Invalid phone number.";
    return true;
  };

  const validateUserName = (value: string | null | undefined) => {
    if (!value) return "User name is required";

    if (/[^a-zA-Z0-9_]/.test(value)) {
      return "User name should contain only letters, numbers, or underscores.";
    }
    return true;
  };

  const onSubmit = async (data: UserRegistrationDto) => {
    setLoading(true);
    authClient.auth_RegisterUser(data).then((response) => {
      if (!response.success) {
        throw new Error(response.errors[0].description);
      }
      navigate("/invest-payment", {
        replace: true,
        state: {
          investmentAmount: 10,
          returnUrl: `${window.location.pathname}?mode=feedback`,
          defaultValues: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
          }
        }
      });
      localStorage.setItem("email", data.email!);
      localStorage.setItem("password", data.password!);
      localStorage.setItem("userName", data.userName!);
      localStorage.setItem("fullName", `${data.firstName} ${data.lastName}`);
      localStorage.removeItem('recommendationData');
      localStorage.setItem("campaignMinInvestment", JSON.stringify(10));
    }).catch((error: any) => {
      setNotificationError({ status: true, text: error?.message ?? "Registration failed." });
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    if (token.token) {
      userClient
        .users_GetUser(token)
        .then((res) => {
          setUser(res);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    Promise.all([
      campaignClient.campaign_GetCampaign(""),
      campaignClient.campaign_GetData(),
    ])
      .then((value) => {
        setCampain(value[0]);
        setInvestmentTypes(value[1].investmentType);
        setApprovedBy(value[1].approvedBy);
        const setFilteredThemes = value[1].theme?.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setAllThemes(setFilteredThemes);

        if (token.token) {
          groupAccountBalanceClient
            .group_GetGroupAccountBalanceByCampaignId(value[0].id!, token)
            .then((responce) => {
              setGroupAccountBalanceClient(responce);
            });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode == "feedback") {
      setInvestFeedbackShow(true);
      proceedToLogin();
    } else {
      setInvestFeedbackShow(false)
    }
  }, [searchParams, setInvestFeedbackShow])

  const Modals: FC = () => {
    return (
      <>
        <BootstrapDialog
          open={termsModalOpen}
          onClose={handleModalClose}
          aria-labelledby="customized-dialog-title"
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Terms and Conditions
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <TermsAndConditions />
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={handleModalClose}>
              Ok
            </Button>
          </DialogActions>
        </BootstrapDialog>

        <BootstrapDialog
          open={privacyModalOpen}
          onClose={handleModalClose}
          aria-labelledby="customized-dialog-title"
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Privacy Policy
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers>
            <PrivacyPolicy />
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={handleModalClose}>
              Ok
            </Button>
          </DialogActions>
        </BootstrapDialog>
      </>
    )
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        margin: "auto",
        paddingBottom: "30px",
        padding: "0 18px",
        paddingTop: "70px",
        width: "1200px"
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {
            investFeedbackShow ? <InvestFeedback
              themes={allThemes!}
              backClick={() => {
                setInvestFeedbackShow(false);
                setInvestmentInProcessShow(false);
                window.history.replaceState(null, "");
              }}
              investmentInProcessShow={() => {
                user.feedback = true;
                navigate("/portfolio")
                setSentFeedback(true);
                setInvestFeedbackShow(false);
                setInvestmentInProcessShow(true);
              }}
            /> : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                style={{ display: "flex", justifyContent: "center" }}
              >
                <Stack spacing={2} width={400}>
                  <ContentTitle
                    title="Sign Up"
                    description={
                      <>
                        <a
                          href="/login"
                          style={{
                            textDecoration: "underline",
                            color: "#007bff",
                          }}
                        >
                          Log in
                        </a>
                        {
                          " to CataCap or else complete the following information to create your account"
                        }
                      </>
                    }
                  />
                  <Divider variant="middle" flexItem>
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu",
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#060A25",
                      }}
                    >
                      Create account with email
                    </Typography>
                  </Divider>
                  <TextField
                    label="First Name"
                    type="text"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                  ></TextField>
                  <TextField
                    label="Last Name"
                    type="text"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                  ></TextField>
                  <TextField
                    label="User Name"
                    type="text"
                    error={!!errors.userName}
                    helperText={errors.userName?.message}
                    {...register("userName", { validate: validateUserName })}
                  ></TextField>
                  <Typography
                    sx={{
                      width: "100%",
                      fontFamily: "Ubuntu",
                      fontSize: 16,
                      fontWeight: 400,
                      color: "#474747",
                    }}
                    align="left"
                  >
                    Email Address
                  </Typography>
                  <TextField
                    sx={{ width: "100%" }}
                    label="Email"
                    type="text"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register("email", { validate: validateEmail })}
                  ></TextField>
                  <Typography
                    sx={{
                      width: "100%",
                      fontFamily: "Ubuntu",
                      fontSize: 16,
                      fontWeight: 400,
                      color: "#474747",
                    }}
                    align="left"
                  >
                    Phone Number
                  </Typography>
                  <TextField
                    sx={{ width: "100%" }}
                    label="Phone Number"
                    type="text"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    {...register("phoneNumber", {
                      validate: validatePhoneNumber,
                    })}
                    onChange={handlePhoneNumberChange}
                  />
                  <Typography
                    sx={{
                      width: "100%",
                      fontFamily: "Ubuntu",
                      fontSize: 16,
                      fontWeight: 400,
                      color: "#474747",
                    }}
                    align="left"
                  >
                    Password
                  </Typography>
                  <FormControl
                    variant="outlined"
                    error={!!errors.password}
                    fullWidth
                  >
                    <InputLabel htmlFor="outlined-adornment-password">
                      Password
                    </InputLabel>
                    <OutlinedInput
                      label="Password"
                      sx={{ width: "100%" }}
                      id="outlined-helper"
                      type={showPassword ? "text" : "password"}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword
                                ? "hide the password"
                                : "display the password"
                            }
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                      {...register("password", {
                        required: "Password is required",
                        pattern: {
                          value:
                            /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?!.* ).{6,}$/,
                          message:
                            "Password must contain one digit, one lowercase letter, one uppercase letter, no space, and it must be 6+ characters long.",
                        },
                      })}
                    />
                    <FormHelperText>
                      {!!errors.password && errors.password?.message}
                    </FormHelperText>
                  </FormControl>

                  <Typography
                    sx={{
                      width: "100%",
                      fontFamily: "Ubuntu",
                      fontSize: 16,
                      fontWeight: 400,
                      color: "#474747",
                    }}
                    align="left"
                  >
                    The new password must contain one digit, one lowercase letter,
                    one uppercase letter, no space, and it must be 6+ characters
                    long.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="success"
                        checked={termsAccepted}
                        onClick={() => {
                          setTermsAccepted(termsAccepted => (!termsAccepted));
                        }}
                      />
                    }
                    label={
                      <>
                        <Typography
                          sx={{
                            fontFamily: "Ubuntu",
                            fontSize: 16,
                            fontWeight: 400,
                            color: "#000000",
                          }}
                        >
                          I accept the&nbsp;
                          <a onClick={handleTermsOpen} href="#">
                            Terms and Conditions
                          </a>
                          &nbsp;and&nbsp;
                          <a onClick={handlePrivacyOpen} href="#">
                            privacy policy
                          </a>
                        </Typography>
                      </>
                    }
                  />
                  <Button
                    disabled={!termsAccepted || Object.keys(errors).length > 0}
                    variant="contained"
                    type="submit"
                    color="primary"
                    sx={{ marginBottom: "1rem !important" }}
                  >
                    Register
                  </Button>
                </Stack>
              </form>
            )
          }
          <Snackbar
            open={notificationError.status}
            autoHideDuration={6000}
            onClose={() => setNotificationError({ status: false })}
          >
            <Alert
              onClose={() => setNotificationError({ status: false })}
              severity="error"
              sx={{ width: "100%" }}
            >
              {notificationError.text}
            </Alert>
          </Snackbar>
          <Modals />
          <DevTool control={control} />
        </>
      )}
    </Box>
  );
};

export default Register;
